/***
*
*   PROFILE
*   Update the user profile or close the account
*
**********/

import Styles from './users.module.scss';
import React, { useState, useEffect, Fragment, useContext, useMemo, useCallback } from 'react';
// Components import.
import {
  AuthContext, ViewContext, Form, Card, Grid, LocalePicker,
  AccountNav, Button, useNavigate, Animate, useAPI, Event, Message, Loader,
  useCan
} from 'components/lib';
import {
  TextField,
  FormControl,
  Box,
  Button as MuiButton,
} from '@mui/material';
import axios from 'axios';

const textFieldProps = {
  size: 'medium',
  className: "!border-slate-200",
  margin: 'dense',
  autoComplete: 'off',
  sx: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'inherit',
      }
    }
  }
};

export function Profile(props) {

  const navigate = useNavigate();
  const permission = useCan('account.profile.view')
  const permissionOwner = useCan('account.profile.owner.view')
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // context
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);

  // fetch
  const user = useAPI('/api/user');

  const [formValues, setFormValues] = useState({
    name: '',
    first_name: '',
    last_name: '',
    zip_code: '',
    address: '',
  });

  useEffect(() => {
    if (user.data) {
      setFormValues({
        name: user.data.name || '',
        first_name: user.data.first_name || '',
        last_name: user.data.last_name || '',
        zip_code: user.data.zip_code || '',
        address: user.data.address || '',
      });
    }
  }, [user]);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'name':
        if (!value?.trim()) return 'Username cannot be blank';
        if (value.length < 3 || value.length > 32) return 'Username must be between 3 and 32 characters';
        if (/\s/.test(value)) return 'Username cannot contain spaces';
        if (!/^[a-zA-Z0-9_]+$/.test(value)) return 'Username should only contain letters, digits, and underscore (_)';
        if (/^\d/.test(value)) return 'Username cannot start with a number';
        break
      case 'last_name':
        if (!value?.trim()) return 'This field is required';
        if (value.length > 50) return 'Must be 50 characters or less';
        if (!/^[a-zA-Z]*$/.test(value)) return 'Must contain only letters';
        break;
      case 'zip_code':
        return /^\d{6}$/.test(value) ? '' : 'Enter a valid 6-digit ZIP code';
      default:
        return '';
    }
  }, []);

  const isFormValid = useMemo(() =>
    formValues.name.trim() &&
    formValues.first_name.trim() &&
    /^\d{6}$/.test(formValues.zip_code) &&
    !Object.values(errors).some(error => error),
    [formValues, errors]
  );

  const handleChange = useCallback((name) => (e) => {
    const { value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  }, [validateField]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      setErrors({
        name: validateField('name', formValues.name),
        last_name: validateField('last_name', formValues.last_name),
        zip_code: validateField('zip_code', formValues.zip_code),
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formValues.name.trim(),
        last_name: formValues.last_name.trim(),
        zip_code: formValues.zip_code,
        ...(formValues.address.trim() && { address: formValues.address.trim() })
      };

      const response = await axios.patch('/api/user', payload);

      if (response.status === 200) {
        viewContext.notification.show(response?.data?.message || "Profile updated successfully", 'success', true);
      }
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || 'Failed to update profile';
      viewContext.notification.show(message, 'error', true);
    } finally {
      setLoading(false);
    }
  }, [formValues, isFormValid, viewContext, validateField]);


  function closeAccount() {
    viewContext.modal.show({

      title: props.t('account.profile.form.close_account.title'),
      form: {},
      buttonText: props.t('account.profile.form.close_account.button'),
      url: (permission && permissionOwner) ? '/api/account' : '/api/user', 
      method: 'DELETE',
      destructive: true,
      text: props.t('account.profile.form.close_account.text'),

    }, () => {

      // delete user
      Event('closed_account');
      localStorage.clear();
      navigate('/signup');

    });

  }

  if (user.loading)
    return <Loader />;

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        <Box>
          <Grid cols='3'>
            <Card
              title={props.t('account.profile.subtitle')}
              className={Styles.card}
              loading={user.loading}
            >
              {user?.data && (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <FormControl fullWidth>
                    <TextField
                      {...textFieldProps}
                      label={props.t('account.profile.form.name.label')}
                      name="name"
                      required
                      value={formValues.name}
                      onChange={handleChange('name')}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      {...textFieldProps}
                      label={props.t('account.profile.form.first_name.label')}
                      name="first_name"
                      required
                      value={formValues.first_name}
                      onChange={handleChange('first_name')}
                      error={!!errors.first_name}
                      helperText={errors.first_name || 'To update your first name, please contact our support team.'}
                      disabled
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      {...textFieldProps}
                      label={props.t('account.profile.form.last_name.label')}
                      name="last_name"
                      required
                      value={formValues.last_name}
                      onChange={handleChange('last_name')}
                      error={!!errors.last_name}
                      helperText={errors.last_name}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      {...textFieldProps}
                      label={props.t('account.profile.form.zip_code.label')}
                      name="zip_code"
                      required
                      type="text"
                      value={formValues.zip_code}
                      onChange={handleChange('zip_code')}
                      error={!!errors.zip_code}
                      helperText={errors.zip_code}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      {...textFieldProps}
                      label={props.t('account.profile.form.address.label')}
                      name="address"
                      multiline
                      rows={3}
                      value={formValues.address}
                      onChange={handleChange('address')}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <MuiButton
                      variant="contained"
                      type='submit'
                      disabled={loading || !isFormValid}
                      className={Styles.submitButton}
                    >
                      {props.t('account.profile.form.button')}
                    </MuiButton>
                  </FormControl>
                </Box>
              )}
              {authContext.permission['user.delete.view'] && (
                <Fragment>
                  <br />
                  <Button
                    textOnly
                    action={closeAccount}
                    text={props.t('account.profile.form.close_account.title')}
                  />
                </Fragment>
              )}
            </Card>

            {authContext.permission['user.orglogo.view'] && (
              <Card
                title={props.t('account.profile.logoUpload.title')}
                className={Styles.card}
                restrictWidth
                restrictHeight
              >
                {!done ? (
                  <Form
                    buttonText={props.t('account.profile.logoUpload.form.button')}
                    url='/api/user/upload'
                    method='POST'
                    inputs={{
                      org_logo: {
                        label: props.t('account.profile.logoUpload.form.org_logo.label'),
                        type: 'file',
                        max: 1,
                        required: true,
                        maxFileSize: 60,
                        onlyImage: true,
                        validUserName: true,
                        disabled: true,
                        imageFullUrl: user.data?.accounts[0]?.org_logo
                      }
                    }}
                    callback={() => setDone(true)}
                  />
                ) : (
                  <Message
                    type='success'
                    title={props.t('account.profile.message.title')}
                    text={props.t('account.profile.message.text')}
                  />
                )}
              </Card>
            )}

            <Card
              title={props.t('account.profile.language.title')}
              className={Styles.card}
              restrictWidth
              restrictHeight
            >
              <LocalePicker className={Styles.language} />
            </Card>
          </Grid>
        </Box>
      </Animate>
    </Fragment>
  );
}
